<script setup lang="ts">
import type { Cluster } from '@/services/types/api.types';

type Idea = Cluster['ideas'][number];

const props = defineProps<{
  modelValue: boolean;
  ideaContext: { cluster: Cluster; idea: Idea } | null;
  contributions: { id: string; content: string | null }[];
  expanded: Set<string>;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'toggle', id: string): void;
}>();

function close() {
  emit('update:modelValue', false);
}
</script>

<template>
  <q-dialog :model-value="modelValue" @update:model-value="emit('update:modelValue', $event)" @hide="close">
    <q-card style="min-width: 640px; max-width: 900px">
      <q-card-section class="text-h6">
        {{ $t('consultation.ai.contributions.dialogTitle') }}
      </q-card-section>
      <q-card-section>
        <i18n-t
          v-if="ideaContext"
          keypath="consultation.ai.contributions.dialogMessage"
          tag="div"
          class="text-body2 q-mb-sm"
        >
          <template #idea>
            <span class="text-weight-bold">{{ ideaContext.idea.representative_idea }}</span>
          </template>
        </i18n-t>
        <q-virtual-scroll :items="contributions" style="max-height: 360px" :virtual-scroll-item-size="88">
          <template #default="{ item }">
            <q-item :key="item.id">
              <q-item-section>
                <q-item-label
                  :class="[
                    'contribution-content',
                    {
                      expanded: expanded.has(item.id),
                      'text-italic text-grey-6': !item.content,
                    },
                  ]"
                >
                  {{ item.content || $t('consultation.ai.contributions.missing') }}
                </q-item-label>
                <div class="row justify-end q-mt-xs" v-if="item.content && item.content.length > 180">
                  <q-btn
                    dense
                    flat
                    size="sm"
                    color="primary"
                    :label="
                      expanded.has(item.id)
                        ? $t('consultation.ai.contributions.collapse')
                        : $t('consultation.ai.contributions.expand')
                    "
                    @click="emit('toggle', item.id)"
                  />
                </div>
              </q-item-section>
            </q-item>
          </template>
          <template #after>
            <div v-if="contributions.length === 0" class="q-pa-md text-grey-6">
              {{ $t('consultation.ai.contributions.empty') }}
            </div>
          </template>
        </q-virtual-scroll>
      </q-card-section>
      <q-card-actions align="right">
        <q-btn flat color="primary" :label="$t('global.cancel')" @click="close" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<style scoped>
.contribution-content {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.contribution-content.expanded {
  -webkit-line-clamp: unset;
}
</style>
